<template>
  <card-block
    class="depending-incident-trigger"
    @click:title="emit('update:open', !open)"
  >
    <template #title>
      <lucide-icon :name="open ? 'chevron-up' : 'chevron-down'" class="mr-2" />
      <span>{{ incident.title }}</span>
    </template>

    <template #actions>
      <button
        class="button-icon card-block__icon"
        type="button"
        @click.prevent="deleteTrigger()"
      >
        <lucide-icon name="trash-2" />
      </button>
    </template>

    <template v-if="open" #default>
      <div>
        <div class="label mt-0">{{ $t("objectNames.incident") }}</div>
        <button-item @click.prevent="emit('select', incident)">
          {{ incident.title }}
        </button-item>
      </div>

      <div>
        <h3 class="label mt-0">
          {{ $t("objectNames.condition") }}
        </h3>
        <button-item
          v-if="mainCondition"
          :selected="selectedCondition?.id == mainCondition.id"
          @click.prevent="emit('select', mainCondition)"
        >
          <condition-label
            :condition-id="mainCondition.id"
            :activator-hidden="true"
          />
        </button-item>
      </div>

      <div class="depending-incident-trigger__line">
        <span>{{ $t("labels.occurrenceProbability") }}{{ $t(":") }}</span>

        <input-number
          :min="0"
          :max="100"
          :value="trigger.chance"
          step="any"
          @update:value="updateTriggerKey('chance', $event)"
        />
        <span>%</span>
      </div>

      <details class="mt-2">
        <summary class="depending-incident-trigger__extra">
          <span class="label" style="display: inline">{{
            $t("labels.additionalConditions")
          }}</span>
        </summary>
        <condition-list
          :src-type="trigger.type"
          :src-id="trigger.id"
          :excluded-conditions="mainCondition ? [mainCondition.id] : []"
          :selected-condition="selectedCondition"
          class="mt-2"
          @select="emit('select', $event)"
        />
      </details>
    </template>
  </card-block>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useIncidentTriggerStore } from "../incidentTrigger/incidentTriggers.store";

import CardBlock from "@core/ui/CardBlock.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";

import ConditionList from "@editor/items/condition/ConditionList.vue";
import ConditionLabel from "@editor/items/condition/ConditionLabel.vue";

import type { IncidentItem } from "./IncidentItem";
import type { ActionItem } from "../action/ActionItem";
import type { IncidentTriggerItem } from "@editor/items/incidentTrigger/IncidentTriggerItem";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";
import type { DependingIncident } from "./DependingIncident";

const props = withDefaults(
  defineProps<{
    source: ActionItem | IncidentItem;
    dependingIncident: DependingIncident;
    selectedCondition?: { type: "condition"; id: number };
    open?: boolean;
  }>(),
  {
    selectedCondition: undefined,
    open: true
  }
);

const emit = defineEmits<{
  (event: "select", condition: ConditionItem | IncidentItem): void;
  (event: "update:open", value: boolean): void;
}>();

const incidentTriggerStore = useIncidentTriggerStore();

const trigger = computed(() => {
  return props.dependingIncident.trigger;
});

const incident = computed(() => {
  return props.dependingIncident.incident;
});

const mainCondition = computed(() => {
  return props.dependingIncident.mainCondition;
});

function updateTriggerKey<K extends keyof IncidentTriggerItem>(
  key: K,
  value: IncidentTriggerItem[K]
) {
  incidentTriggerStore.updateKey(trigger.value.id, key, value);
}

function deleteTrigger() {
  incidentTriggerStore.delete({ id: trigger.value.id });
}
</script>

<style lang="scss">
.depending-incident-trigger {
  &__icon {
    font-size: 16px;
    padding: 0.25rem;
    padding-right: 1rem;
  }

  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 4rem 1rem;
    gap: 0.5rem;
    align-items: center;
  }

  &__extra {
    &::marker {
      color: var(--color-text-light);
    }
    &:hover {
      cursor: pointer;
      .label {
        color: var(--color-text);
      }
    }
  }
}
</style>
